import { axios } from "@axios";
import { useQuery, useMutation, MutationOptions } from "react-query";
import { ExtractFnReturnType, QueryConfig } from "../../lib/react-query";

export const getUser = (userId: string): Promise<any> => {
  return axios.get(`/user/user-details/${userId}`);
};

export const getAllOffers = (userId: string, params: any): Promise<any> => {
  return axios.get(`/offer/get-all-offers/${userId}`, { params });
};
export const getActiveOffers = (params: any): Promise<any> => {
  return axios.get(`/offer/get-active-offers`, { params });
};
export const getAllApplications = (
  userId: string,
  params: any
): Promise<any> => {
  return axios.get(`/application/get-all-application/${userId}`, { params });
};

export const getAllOffersCount = (userId: string): Promise<any> => {
  return axios.get(`/offer/get-all-offers-count/${userId}`);
};

export const getAllApplicationsCounts = (
  userId: string
): Promise<any> => {
  return axios.get(`/application/get-all-application-count/${userId}`);
};

type GetAllOffersCountQueryFnType = typeof getAllOffersCount;

export const updateUser = ({
  id,
  updateData,
}: {
  id: string;
  updateData: any;
}): Promise<any> => {
  return axios.put(`/user/update-user`, updateData, {
    params: { id },
  });
};

type GetAllOffersQueryFnType = typeof getAllOffers;

export const useAllOffersQuery = ({
  userId,
  params,
  config,
}: {
  userId: string;
  params: any;
  config?: QueryConfig<GetAllOffersQueryFnType>;
}) => {
  return useQuery<ExtractFnReturnType<GetAllOffersQueryFnType>>({
    queryKey: ["allUsersCount", userId, params],
    queryFn: () => getAllOffers(userId, params),
    select: (res: any) => res.data,
    ...config,
  });
};

export const useAllOffersCountQuery = ({
  userId,
  config,
}: {
  userId: string;
  config?: QueryConfig<GetAllOffersCountQueryFnType>;
}) => {
  return useQuery<ExtractFnReturnType<GetAllOffersCountQueryFnType>>({
    queryKey: ["allUsersStatusCount", userId], // no params
    queryFn: () => getAllOffersCount(userId),
    select: (res: any) => res.data,
    ...config,
  });
};

type GetActiveOffersQueryFnType = typeof getActiveOffers;

export const useActiveOffersQuery = ({
  params,
  config,
}: {
  params: any;
  config?: QueryConfig<GetActiveOffersQueryFnType>;
}) => {
  return useQuery<ExtractFnReturnType<GetActiveOffersQueryFnType>>({
    queryKey: ["allUsersCount", params],
    queryFn: () => getActiveOffers(params),
    select: (res: any) => res.data,
    ...config,
  });
};

type UpdateUserFnType = typeof updateUser;

export const useUpdateUserMutation = (
  options: MutationOptions<
    Awaited<ReturnType<UpdateUserFnType>>,
    unknown,
    Parameters<UpdateUserFnType>[0]
  >
) => {
  return useMutation<
    Awaited<ReturnType<UpdateUserFnType>>,
    unknown,
    Parameters<UpdateUserFnType>[0]
  >({
    mutationFn: updateUser,
    ...options,
  });
};

type GetAllApplicationsQueryFnType = typeof getAllOffers;

export const useAllApplicationsQuery = ({
  userId,
  params,
  config,
}: {
  userId: string;
  params?: any;
  config?: QueryConfig<typeof getAllApplications>;
}) => {
  return useQuery<ExtractFnReturnType<typeof getAllApplications>>({
    queryKey: ["allApplicationCount", userId, params],
    queryFn: () => getAllApplications(userId, params),
    select: (res: any) => res.data,
    ...config,
  });
};

type GetAllApplicationsCountQueryFnType = typeof getAllApplicationsCounts;

export const useAllApplicationsCountQuery = ({
  userId,
  config,
}: {
  userId: string;
  config?: QueryConfig<GetAllApplicationsCountQueryFnType>;
}) => {
  return useQuery<ExtractFnReturnType<GetAllApplicationsCountQueryFnType>>({
    queryKey: ["allApplicationStatusCount", userId],
    queryFn: () => getAllApplicationsCounts(userId),
    select: (res: any) => res.data,
    ...config,
  });
};


type GetUserQueryFnType = typeof getUser;

export const useGetUserQuery = ({
  userId,
  params,
  config,
}: {
  userId: string;
  params: any;
  config?: QueryConfig<GetUserQueryFnType>;
}) => {
  return useQuery<ExtractFnReturnType<GetUserQueryFnType>>({
    queryKey: ["getUser", userId],
    queryFn: () => getUser(userId),
    select: (res: any) => res.data,
    enabled: !!userId, // ensures query runs only if userId is defined
    ...config,
  });
};
